import { useEffect, useMemo } from 'react'
import { GlobalEvents } from '@highlight/ipc'
import { useBrowserTabs } from '../../../../../hooks/browser-tabs'
import { useModels } from '../../../../../hooks/useModels'
import { MentionableBrowserTab } from '../../../../../types'

/** The mentionable model list, kept fresh across backend model refetches. */
export const useLanguageModels = () => {
  const { models, fetchModels } = useModels()

  useEffect(() => {
    GlobalEvents.on('models-refetched', fetchModels)
    return () => {
      GlobalEvents.off('models-refetched', fetchModels)
    }
  }, [fetchModels])

  return models
}

type BrowserTab = { id: number; title: string; base64Favicon?: string; faviconUrl?: string }

const toMentionable = (tab: BrowserTab): MentionableBrowserTab => ({
  label: tab.title,
  id: tab.id,
  value: tab.id.toString(),
  type: 'browser_tab',
  images: { image: tab.base64Favicon || tab.faviconUrl || null },
})

/** Active tabs first, then the rest of every window — each tab listed once. */
export const useMentionableBrowserTabs = (): Array<MentionableBrowserTab> => {
  const { data } = useBrowserTabs()

  return useMemo(() => {
    const active: Array<BrowserTab> = data?.active ?? []
    const windows: Array<Array<BrowserTab>> = data?.windows ?? []
    const activeIds = new Set(active.map((tab) => tab.id))
    return [...active, ...windows.flat().filter((tab) => !activeIds.has(tab.id))].map(toMentionable)
  }, [data])
}
